import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChefHat } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';
import { ordenCocinaV2API } from '../api/api';
import { hoyMadrid } from '../utils/fechas';

// Descarga de la orden de cocina (nueva): la de este presupuesto (con el
// handler de siempre de la ficha) o varias en un solo PDF (lote del servidor,
// eventos confirmados o aceptados entre dos fechas, una orden por jornada).
// Todo es solo lectura: el servidor genera las órdenes con los datos vivos.
// Servicios sin hora (M1): el lote los devuelve en `avisos_horarios` y se
// enseñan mientras se genera, con el enlace para poner las horas; las órdenes
// salen igualmente.
// Sin presupuesto (desde el Dashboard) solo se ofrece el bloque por fechas,
// con la semana de hoy por defecto.

const MAX_DIAS = 62;
const SONDEO_MS = 800;
const TEXTO_NO_DISPONIBLE = 'La descarga ya no está disponible, vuelve a lanzarla';

const pad2 = (n) => String(n).padStart(2, '0');

// 'AAAA-MM-DD' -> milisegundos UTC (null si no es una fecha válida)
function msDeIso(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').slice(0, 10));
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function isoDeMs(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

// Lunes y domingo de la semana de una fecha (o de hoy si no hay)
function semanaDe(fecha) {
  const base = msDeIso(fecha) ?? msDeIso(hoyMadrid());
  const dia = new Date(base).getUTCDay(); // 0 domingo ... 6 sábado
  const lunes = base - ((dia + 6) % 7) * 86400000;
  return { desde: isoDeMs(lunes), hasta: isoDeMs(lunes + 6 * 86400000) };
}

// 'AAAA-MM-DD...' -> 'dd/mm/aaaa'; cualquier otro texto se deja tal cual
function fechaLegible(valor) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(valor || ''));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : (valor || '');
}

// Detalle de un error del API (también cuando la respuesta es un blob)
async function detalleError(error) {
  const datos = error?.response?.data;
  try {
    if (datos && typeof datos.text === 'function') {
      const texto = await datos.text();
      const json = texto ? JSON.parse(texto) : null;
      if (typeof json?.detail === 'string') return json.detail;
    } else if (typeof datos?.detail === 'string') {
      return datos.detail;
    }
  } catch (e) {
    // Sin detalle legible
  }
  return null;
}

function descargarBlob(respuesta, nombre) {
  const tipo = respuesta.headers?.['content-type'] || 'application/pdf';
  const url = window.URL.createObjectURL(new Blob([respuesta.data], { type: tipo }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// Cancela un lote en el servidor sin esperar respuesta (idempotente)
function cancelarEnServidor(id) {
  if (!id) return;
  ordenCocinaV2API.loteCancelar(id).catch(() => {});
}

const mismaOrden = (a, b) => (
  String(a?.numero) === String(b?.numero) && a?.etiqueta === b?.etiqueta && a?.fecha === b?.fecha
);

function ContenidoModal({ onClose, presupuesto, onDescargarEsta, esperarAutoguardado }) {
  const [vista, setVista] = useState('opciones'); // opciones | progreso | listo | error
  const [rango, setRango] = useState(() => semanaDe(presupuesto?.fecha_evento));
  const [aviso, setAviso] = useState('');
  const [lanzando, setLanzando] = useState(false);
  const [lote, setLote] = useState(null); // respuesta de loteCrear
  const [estado, setEstado] = useState(null); // última respuesta de loteEstado
  const [mensajeError, setMensajeError] = useState('');
  const [descargandoPdf, setDescargandoPdf] = useState(false);

  // Para cancelar el lote si el modal se cierra o se desmonta en mitad
  const loteEnCursoRef = useRef(null);
  const sondeandoRef = useRef(false);

  const loteId = lote?.lote_id || null;

  // Jornadas con fecha (multievento): rango de "Todas las jornadas de este evento"
  const fechasJornadas = (presupuesto?.es_multi_evento ? (presupuesto?.jornadas || []) : [])
    .map((j) => String(j?.fecha || '').slice(0, 10))
    .filter((f) => msDeIso(f) !== null)
    .sort();
  const puedeTodasJornadas = fechasJornadas.length >= 2;

  // Validación del rango en cliente
  const msDesde = msDeIso(rango.desde);
  const msHasta = msDeIso(rango.hasta);
  let errorRango = '';
  if (msDesde === null || msHasta === null) {
    errorRango = 'Indica las dos fechas.';
  } else if (msHasta < msDesde) {
    errorRango = 'La fecha Hasta no puede ser anterior a Desde.';
  } else if ((msHasta - msDesde) / 86400000 > MAX_DIAS) {
    errorRango = `Como máximo ${MAX_DIAS} días entre las dos fechas.`;
  }

  // Al desmontar en mitad de un lote, se cancela
  useEffect(() => () => {
    cancelarEnServidor(loteEnCursoRef.current);
    loteEnCursoRef.current = null;
  }, []);

  const pedirPdf = useCallback(async (datosLote) => {
    const respuesta = await ordenCocinaV2API.lotePdf(datosLote.lote_id);
    descargarBlob(respuesta, `ordenes_cocina_${datosLote.desde}_${datosLote.hasta}.pdf`);
  }, []);

  // Sondeo del estado del lote mientras se genera
  useEffect(() => {
    if (vista !== 'progreso' || !loteId) return undefined;
    let terminado = false;
    const intervalo = setInterval(async () => {
      if (terminado || sondeandoRef.current) return;
      sondeandoRef.current = true;
      try {
        const { data } = await ordenCocinaV2API.loteEstado(loteId);
        if (terminado) return;
        setEstado(data);
        if (data?.estado === 'listo') {
          terminado = true;
          clearInterval(intervalo);
          loteEnCursoRef.current = null;
          try {
            await pedirPdf({ lote_id: loteId, desde: data.desde || lote?.desde, hasta: data.hasta || lote?.hasta });
            setVista('listo');
          } catch (error) {
            setMensajeError((await detalleError(error)) || TEXTO_NO_DISPONIBLE);
            setVista('error');
          }
        } else if (data?.estado === 'error' || data?.estado === 'cancelado') {
          terminado = true;
          clearInterval(intervalo);
          loteEnCursoRef.current = null;
          setMensajeError(data?.mensaje || (data?.estado === 'cancelado' ? 'La descarga se ha cancelado.' : TEXTO_NO_DISPONIBLE));
          setVista('error');
        }
      } catch (error) {
        terminado = true;
        clearInterval(intervalo);
        loteEnCursoRef.current = null;
        const detalle = error?.response?.status === 404 ? null : await detalleError(error);
        setMensajeError(detalle || TEXTO_NO_DISPONIBLE);
        setVista('error');
      } finally {
        sondeandoRef.current = false;
      }
    }, SONDEO_MS);
    return () => {
      terminado = true;
      clearInterval(intervalo);
    };
  }, [vista, loteId, lote?.desde, lote?.hasta, pedirPdf]);

  const lanzarLote = async (datos) => {
    setAviso('');
    setLanzando(true);
    try {
      if (typeof esperarAutoguardado === 'function') await esperarAutoguardado();
      const { data } = await ordenCocinaV2API.loteCrear(datos);
      if (!data?.lote_id || !data?.total) {
        setAviso(data?.mensaje || 'No hay órdenes que descargar entre esas fechas.');
        return;
      }
      loteEnCursoRef.current = data.lote_id;
      setLote(data);
      setEstado({ estado: 'en_curso', total: data.total, hechas: 0, actual: null, errores: [] });
      setVista('progreso');
    } catch (error) {
      setAviso((await detalleError(error)) || 'No se pudo lanzar la descarga. Inténtalo de nuevo.');
    } finally {
      setLanzando(false);
    }
  };

  const handleDescargarTodas = () => {
    if (errorRango) return;
    lanzarLote({ desde: rango.desde, hasta: rango.hasta });
  };

  const handleTodasJornadas = () => {
    lanzarLote({
      desde: fechasJornadas[0],
      hasta: fechasJornadas[fechasJornadas.length - 1],
      presupuesto_ids: [presupuesto?.id],
    });
  };

  const handleCancelar = () => {
    cancelarEnServidor(loteEnCursoRef.current);
    loteEnCursoRef.current = null;
    setLote(null);
    setEstado(null);
    setVista('opciones');
  };

  const handleCerrar = () => {
    cancelarEnServidor(loteEnCursoRef.current);
    loteEnCursoRef.current = null;
    onClose();
  };

  const handleDescargarDeNuevo = async () => {
    if (!lote) return;
    setDescargandoPdf(true);
    try {
      await pedirPdf({ lote_id: lote.lote_id, desde: estado?.desde || lote.desde, hasta: estado?.hasta || lote.hasta });
    } catch (error) {
      setMensajeError((await detalleError(error)) || TEXTO_NO_DISPONIBLE);
      setVista('error');
    } finally {
      setDescargandoPdf(false);
    }
  };

  const total = estado?.total || lote?.total || 0;
  const hechas = estado?.hechas || 0;
  const errores = estado?.errores || [];
  const porcentaje = total > 0 ? Math.min(100, Math.round((hechas / total) * 100)) : 0;

  const estadoDeOrden = (orden, i) => {
    if (errores.some((e) => mismaOrden(e, orden))) return 'error';
    if (i < hechas) return 'hecha';
    if (i === hechas) return 'en curso';
    return 'pendiente';
  };
  const claseEstado = {
    hecha: 'text-emerald-700',
    'en curso': 'text-orange-700 font-medium',
    pendiente: 'text-gray-400',
    error: 'text-red-600 font-medium',
  };

  const avisosHoras = lote?.avisos_horarios || [];
  const bloqueHoras = avisosHoras.length > 0 && (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="orden-nueva-avisos-horas">
      <p className="text-sm font-medium text-amber-800 mb-1">Servicios sin hora o que no cuadran (las órdenes salen igualmente)</p>
      <ul className="space-y-1 text-xs text-amber-800">
        {avisosHoras.slice(0, 15).map((a, i) => (
          <li key={`${a.presupuesto_id}-${i}`}>
            {a.numero} · {a.texto}{' '}
            <a href={`/presupuestos/${a.presupuesto_id}?pestana=logistica&nivel=horario`} target="_blank" rel="noreferrer"
              className="font-semibold underline">Poner las horas</a>
          </li>
        ))}
        {avisosHoras.length > 15 && <li>Y {avisosHoras.length - 15} más.</li>}
      </ul>
    </div>
  );

  const bloqueErrores = errores.length > 0 && (
    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
      <p className="text-sm font-medium text-red-700 mb-1">No se pudieron generar</p>
      <ul className="space-y-1 text-xs text-red-700">
        {errores.map((e) => (
          <li key={`${e.numero}-${e.etiqueta}-${e.fecha}`}>
            {e.numero} · {e.etiqueta} · {fechaLegible(e.fecha)}: {e.motivo}
          </li>
        ))}
      </ul>
    </div>
  );

  const botonPrimario = 'px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed';
  const botonSecundario = 'px-4 py-2 bg-white text-orange-700 border border-orange-400 rounded-lg hover:bg-orange-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed';
  const botonNeutro = 'px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm';

  return (
    <Dialog open onOpenChange={(abierto) => { if (!abierto) handleCerrar(); }}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-lg max-h-[85vh] overflow-y-auto bg-white" data-testid="orden-nueva-modal">
        <DialogTitle className="flex items-center gap-2 text-orange-700">
          <ChefHat className="w-5 h-5" />
          {presupuesto ? 'Orden de cocina (nueva)' : 'Órdenes de cocina'}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {presupuesto
            ? 'Descarga la orden de cocina de este presupuesto o varias órdenes en un solo PDF.'
            : 'Descarga varias órdenes de cocina en un solo PDF.'}
        </DialogDescription>

        {vista === 'opciones' && (
          <div className="space-y-4">
            {presupuesto && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-900">Este presupuesto</p>
              <div className="flex flex-wrap gap-2">
                <div className="flex flex-col">
                  <button
                    type="button"
                    className={botonPrimario}
                    onClick={onDescargarEsta}
                    disabled={lanzando}
                    data-testid="orden-nueva-descargar-esta"
                  >
                    Descargar esta orden
                  </button>
                  {presupuesto?.es_multi_evento && (presupuesto?.jornadas || []).length > 0 && (
                    <span className="text-[11px] text-gray-500 mt-1">Te pedirá la jornada</span>
                  )}
                </div>
                {puedeTodasJornadas && (
                  <button
                    type="button"
                    className={`${botonSecundario} self-start`}
                    onClick={handleTodasJornadas}
                    disabled={lanzando}
                    data-testid="orden-nueva-todas-jornadas"
                  >
                    Todas las jornadas de este evento
                  </button>
                )}
              </div>
            </div>
            )}

            <div className="rounded-lg border border-gray-200 p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-900">Varias órdenes por fechas</p>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm text-gray-700">
                  Desde
                  <input
                    type="date"
                    value={rango.desde}
                    onChange={(e) => { setAviso(''); setRango((r) => ({ ...r, desde: e.target.value })); }}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                    data-testid="orden-nueva-desde"
                  />
                </label>
                <label className="text-sm text-gray-700">
                  Hasta
                  <input
                    type="date"
                    value={rango.hasta}
                    onChange={(e) => { setAviso(''); setRango((r) => ({ ...r, hasta: e.target.value })); }}
                    className="mt-1 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                    data-testid="orden-nueva-hasta"
                  />
                </label>
              </div>
              <p className="text-xs text-gray-500">
                Eventos confirmados o aceptados entre esas fechas, una orden por jornada, todas sus hojas en un solo PDF.
              </p>
              {errorRango && <p className="text-xs text-red-600">{errorRango}</p>}
              <button
                type="button"
                className={botonPrimario}
                onClick={handleDescargarTodas}
                disabled={!!errorRango || lanzando}
                data-testid="orden-nueva-descargar-todas"
              >
                {lanzando ? 'Preparando...' : 'Descargar todas'}
              </button>
            </div>

            {aviso && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="orden-nueva-aviso">
                {aviso}
              </div>
            )}
          </div>
        )}

        {vista === 'progreso' && (
          <div className="space-y-3">
            <Progress value={porcentaje} className="bg-orange-100 [&>div]:bg-orange-500" />
            <p className="text-sm text-gray-800">
              Generando orden {Math.min(hechas + 1, total)} de {total}
            </p>
            {estado?.actual && <p className="text-xs text-gray-500">{estado.actual}</p>}
            {bloqueHoras}
            {(lote?.ordenes || []).length > 0 && (
              <ul className="max-h-60 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-lg text-xs">
                {lote.ordenes.map((orden, i) => {
                  const est = estadoDeOrden(orden, i);
                  return (
                    <li key={`${orden.numero}-${orden.etiqueta}-${orden.fecha}`} className="flex items-center justify-between gap-2 px-3 py-1.5">
                      <span className="text-gray-700 truncate">
                        {fechaLegible(orden.fecha)} · {orden.numero} · {orden.etiqueta}
                        {orden.cliente ? ` · ${orden.cliente}` : ''}
                      </span>
                      <span className={`shrink-0 ${claseEstado[est]}`}>{est}</span>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="flex justify-end">
              <button type="button" className={botonNeutro} onClick={handleCancelar} data-testid="orden-nueva-cancelar">
                Cancelar
              </button>
            </div>
          </div>
        )}

        {vista === 'listo' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-800">
              Descargadas {total - errores.length} órdenes{estado?.paginas ? ` (${estado.paginas} páginas)` : ''}.
            </p>
            {bloqueHoras}
            {bloqueErrores}
            <div className="flex justify-end gap-2">
              <button type="button" className={botonSecundario} onClick={handleDescargarDeNuevo} disabled={descargandoPdf}>
                {descargandoPdf ? 'Descargando...' : 'Descargar de nuevo'}
              </button>
              <button type="button" className={botonNeutro} onClick={handleCerrar}>
                Cerrar
              </button>
            </div>
          </div>
        )}

        {vista === 'error' && (
          <div className="space-y-3">
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {mensajeError || TEXTO_NO_DISPONIBLE}
            </div>
            {bloqueErrores}
            <div className="flex justify-end">
              <button
                type="button"
                className={botonNeutro}
                onClick={() => { setLote(null); setEstado(null); setMensajeError(''); setVista('opciones'); }}
              >
                Volver
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// El contenido solo se monta con el modal abierto: cada apertura empieza en
// "opciones" con las fechas recalculadas y, al cerrarse, se para el sondeo.
export default function OrdenCocinaDescargaModal({ open, onClose, presupuesto, onDescargarEsta, esperarAutoguardado }) {
  if (!open) return null;
  return (
    <ContenidoModal
      onClose={onClose}
      presupuesto={presupuesto}
      onDescargarEsta={onDescargarEsta}
      esperarAutoguardado={esperarAutoguardado}
    />
  );
}
